import type {
  ApiLabelRuntaskMoreSchemaResult,
  ApiLabelRuntaskSchemaResult,
  ApiRunbookListSchema,
  ApiRunbookListSchemaResult,
  ApiRunbookMoreSchema,
  ApiRunbookMoreSchemaResult,
  ApiRunbookOptionsSchemaResult,
} from '@/interfaces/Api.generated.interfaces'
import type { LanguageCode } from '@/interfaces/Language.interfaces'

export type RunbookId = number

export type GetRunbookAttachmentsResponse = RunbookAttachment

export interface RunbookAttachment {
  allowed_perimeter_ids: number[]
  allowed_tag_ids: number[]
}

export enum RunbookPerimeterType {
  INTERNAL = 'INTERNAL',
  PROVIDER = 'PROVIDER',
  THIRD_PARTY = '3RDPARTY',
  APPLICATION = 'APPLICATION',
}

export enum RunbookPeriodicity {
  HOURLY = 'HOURLY',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  SEMESTERLY = 'SEMESTERLY',
  YEARLY = 'YEARLY',
}

export interface RunbookLocalTag {
  tag: string
  is_root?: boolean
}

export interface RunbookLocalPerimeter {
  name: string
  type: RunbookPerimeterType | null
}

export type RunbookStatusCount = number[]

export interface RunbookRow {
  id: RunbookId | string
  name: string
  nb_runtasks: number
  nb_runtask_groups?: number
  local_tag?: RunbookLocalTag | null
  local_perimeter?: RunbookLocalPerimeter | null
  status_365?: RunbookStatusCount | null
  status_p?: RunbookStatusCount | null
  loading_stats?: boolean
  edit_right?: boolean
  delete_right?: boolean
  view_right?: boolean
}

export interface RunbookLabelRow {
  id: number
  label_id: number
  name: string
  nb_runtasks: number
  nb_runtask_groups?: number
  status_365?: RunbookStatusCount | null
  status_p?: RunbookStatusCount | null
  loading_stats?: boolean
  edit_right?: boolean
  delete_right?: boolean
  view_right?: boolean
}

export interface RunbookScore {
  id: RunbookId | string
  status_365: RunbookStatusCount | null
  status_p: RunbookStatusCount | null
}

export type ApiRunbookRowSchema = Omit<ApiRunbookListSchema, 'id'> & { id?: number | string }

export type ApiRunbooksResult = Omit<ApiRunbookListSchemaResult, 'objects'> & {
  message?: string
  objects?: ApiRunbookRowSchema[]
  all?: ApiRunbookRowSchema | null
}

export type ApiRunbookLabelsResult = ApiLabelRuntaskSchemaResult & { message?: string }

export type ApiRunbookScoreSchema = Omit<ApiRunbookMoreSchema, 'id' | 'status_365' | 'status_p'> & {
  id?: number | string
  status_365?: number[] | null
  status_p?: number[] | null
}

export type ApiRunbookScoresResult = Omit<ApiRunbookMoreSchemaResult, 'objects'> & {
  message?: string
  objects?: ApiRunbookScoreSchema[]
  all?: ApiRunbookScoreSchema | null
}

export type ApiRunbookLabelsScoresResult = Omit<ApiLabelRuntaskMoreSchemaResult, 'objects'> & {
  message?: string
  objects?: ApiRunbookScoreSchema[]
}

export type ApiRunbookOptionsResult = ApiRunbookOptionsSchemaResult & { message?: string }

export interface RunbookOption {
  id: number
  name: string
  identifier?: string
}

export interface RunbookPerimeterOption {
  id: number
  name: string
}

export interface RunbooksListResult {
  runbooks: RunbookRow[]
  aggregate: RunbookRow | null
  addRight: boolean
}

export interface RunbookScoresResult {
  scores: RunbookScore[]
  aggregate: RunbookScore | null
}

export interface RunbookExportReportTask {
  id: number
  name: string
  identifier?: string
  start_date?: string | null
  end_date?: string | null
}

export interface RunbookExportReportPayload {
  run_task: number
  start_date: string
  end_date: string
  lang: LanguageCode
  text_size: number
  logo: boolean
  files: boolean
  proofs: boolean
  manager: boolean
}

export interface RunbookTaskMeasure {
  name: string
}

export interface RunbookTaskManager {
  name: string | null
  icon?: string | null
}

export interface RunbookTaskRow {
  id: number
  identifier: string
  name: string
  measure?: RunbookTaskMeasure | null
  ug?: RunbookTaskManager | null
  periodicity?: RunbookPeriodicity | null
  effort?: number | null
  delete_right?: boolean
}

export interface RunbookFilterableNamedEntity {
  name?: string | null
}

export interface RunbookFilterableRow {
  periodicity?: RunbookPeriodicity | null
  measure?: RunbookFilterableNamedEntity | null
  labels?: RunbookFilterableNamedEntity[] | null
  ug?: RunbookFilterableNamedEntity | null
  have_to_complete?: boolean
}

export interface RunbookFilterOptions {
  periodicities: RunbookPeriodicity[]
  measures: string[]
  labels: string[]
  managers: string[]
}

export interface RunbookSelectedFilters {
  periodicities: string[]
  measures: string[]
  labels: string[]
  managers: string[]
  missingEntries: boolean[]
}

export interface RunbookFiltersStoreEntry {
  customerId: number
  runbookKey: string
  filters: RunbookSelectedFilters
}
